/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { Grid, IconButton, Typography } from "@mui/material";
import { TextFieldWithHelperText } from "../../../../components/TextFieldWithHelperText";
import { submitRequestDialogStyles as styles } from "../../styles";
import ConvoyDialog from "../../../../components/ConvoyDialog";
import { toCamelCase } from "../../../../utils/helperFunctions";
import { useGetEnvs } from "../../../../api/envs";
import {
  useListALLCommonScripts,
  useSubmitOsBuild,
} from "../../../../api/softwareImages";
import { PlaylistAddRounded } from "@mui/icons-material";
import ScriptItem from "./ScriptItem";
import CloudSpecificItem from "./CloudSpecificItem";

const SubmitOsPatchRequestDialog = ({
  open,
  resetDialog,
  callback,
  displayNotification,
  initPayload,
  availableClouds,
}) => {
  const [payload, setPayload] = useState({});

  const {
    postData: submitOsBuild,
    response: submitOsBuildResp,
    responseStatus: submitOsBuildStatus,
  } = useSubmitOsBuild();

  const { postData: listAllCommonScripts, response: listAllCommonScriptsResp } =
    useListALLCommonScripts(initPayload?.releaseBranch);

  const { postData: getEnvs, response: getEnvsResp } = useGetEnvs();

  useEffect(() => {
    if (open) {
      getEnvs();
      setPayload(structuredClone(initPayload));
      listAllCommonScripts();
    } else {
      setPayload({});
    }
  }, [open, initPayload]);

  const handleDialogConfirm = () => {
    const reqPayload = structuredClone(payload);
    availableClouds?.forEach((cloud) => {
      if (reqPayload?.tenant?.[cloud.toUpperCase()] === "No Tenant") {
        delete reqPayload.tenant[cloud.toUpperCase()];
      }
    });
    delete reqPayload.dbSoftware;
    submitOsBuild(reqPayload);
    resetDialog();
  };

  /**
   * @param {string} label
   * @returns {object} */
  const textFieldCommonProps = (label) => ({
    fullWidth: true,
    label: label,
    color: "secondary",
    value: payload?.[toCamelCase(label)],
    onChange: (e) => {
      setPayload((prev) => ({
        ...prev,
        [toCamelCase(label)]: e.target.value,
      }));
    },
  });

  const setScriptDetails = (idx) => {
    return (scriptDetails) => {
      setPayload((prev) => ({
        ...prev,
        scripts: [
          ...prev?.scripts?.slice(0, idx),
          scriptDetails,
          ...prev?.scripts?.slice(idx + 1),
        ],
      }));
    };
  };

  const deleteScript = (idx) => {
    return () => {
      setPayload((prev) => ({
        ...prev,
        scripts: [
          ...prev?.scripts?.slice(0, idx),
          ...prev?.scripts?.slice(idx + 1),
        ],
      }));
    };
  };

  useEffect(() => {
    displayNotification(submitOsBuildResp, submitOsBuildStatus, callback);
  }, [submitOsBuildResp, submitOsBuildStatus]);

  const isValidPayload = useMemo(() => {
    if (!payload?.jiraId || !payload?.version || !payload?.versionName) {
      return false;
    }
    const payloodClouds = Object?.keys(payload?.clouds || {});
    const cloudVaildation =
      payloodClouds?.length &&
      payloodClouds?.every((cloud) => {
        const item = payload?.clouds?.[cloud];
        return item?.tenantDomain && item?.dbSoftware?.length;
      });
    if (!cloudVaildation) {
      return false;
    }

    return (
      payload?.scripts?.length &&
      payload?.scripts?.every((scriptItem) => scriptItem?.scriptName)
    );
  }, [payload]);

  return (
    <ConvoyDialog
      open={open}
      title="Submit DB Patch Request"
      handleDialogConfirm={handleDialogConfirm}
      resetDialog={resetDialog}
      disabled={!isValidPayload}
    >
      <Grid container spacing={1} sx={styles.gridContainer}>
        <Grid item xs={3}>
          <TextFieldWithHelperText
            {...textFieldCommonProps("Engine")}
            disabled
          />
        </Grid>
        <Grid item xs={3}>
          <TextFieldWithHelperText
            {...textFieldCommonProps("Os Name")}
            disabled
          />
        </Grid>
        <Grid item xs={3}>
          <TextFieldWithHelperText
            {...textFieldCommonProps("Old Version")}
            disabled
          />
        </Grid>

        <Grid item xs={3}>
          <TextFieldWithHelperText
            {...textFieldCommonProps("Old Version Name")}
            disabled
          />
        </Grid>
        <Grid item xs={3}>
          <TextFieldWithHelperText
            {...textFieldCommonProps("Release Branch")}
            disabled
          />
        </Grid>
        <Grid item xs={3}>
          <TextFieldWithHelperText {...textFieldCommonProps("Jira Id")} />
        </Grid>
        <Grid item xs={3}>
          <TextFieldWithHelperText {...textFieldCommonProps("Version")} />
        </Grid>
        <Grid item xs={3}>
          <TextFieldWithHelperText {...textFieldCommonProps("Version Name")} />
        </Grid>

        {availableClouds?.map((cloud, i) => (
          <CloudSpecificItem
            payload={payload}
            setPayload={setPayload}
            key={i}
            cloud={cloud}
            getEnvsResp={getEnvsResp}
          />
        ))}
        <Grid item xs={11}>
          <Typography variant="h5">Scripts</Typography>
        </Grid>
        <Grid item xs={1}>
          <IconButton
            size="small"
            onClick={() =>
              setPayload((prev) => ({
                ...prev,
                scripts: [
                  ...(prev?.scripts || []),
                  { cloud: "ALL", rebootTime: 0, scriptName: "" },
                ],
              }))
            }
          >
            <PlaylistAddRounded />
          </IconButton>
        </Grid>
        {payload?.scripts?.map((scriptDetails, i) => (
          <ScriptItem
            key={i}
            listAllCommonScriptsResp={listAllCommonScriptsResp}
            setScriptDetails={setScriptDetails(i)}
            payload={payload}
            setPayload={setPayload}
            idx={i}
            scriptDetails={scriptDetails}
            deleteScript={deleteScript(i)}
          />
        ))}
      </Grid>
    </ConvoyDialog>
  );
};

export default SubmitOsPatchRequestDialog;

export const useSubmitOsPatchRequestDialog = (
  callback = () => {},
  displayNotification = () => {}
) => {
  const [open, setOpen] = useState(false);
  const [initPayload, setInitPayload] = useState({});
  const [availableClouds, setAvailableClouds] = useState([]);

  return {
    submitOsPatchRequestDialog: (
      <SubmitOsPatchRequestDialog
        open={open}
        resetDialog={() => setOpen(false)}
        callback={callback}
        displayNotification={displayNotification}
        initPayload={initPayload}
        availableClouds={availableClouds}
      />
    ),
    setOpenSubmitOsPatchRequestDialog: (initPayload, availableClouds) => {
      setOpen(true);
      setInitPayload(initPayload || {});
      setAvailableClouds(availableClouds || []);
    },
  };
};
